/// <reference lib="dom"/>

import React from 'react'
import clsx from 'clsx'
import Platform from 'utils/Platform'
import TextField, { TextFieldProps } from 'components/TextField'
import stylesGlobal from 'stylesGlobal.module.scss'
import styles from './TextFieldNumber.module.scss'

const CHARACTER_LIMIT = 8
const TEXT_INPUT_PROPS = {
  inputProps: { type: 'text', pattern: '[0-9]*', maxLength: 8, inputMode: 'numeric' },
  InputProps: {
    classes: {
      input: clsx(stylesGlobal.TextCenter, styles.TextFieldNumberInput)
    }
  }
}
const DEFAULT_PLACEHOLDER = '00000000'

const CLASSNAMES = {
  PlaceholderText: clsx(
    stylesGlobal.PositionAbsolute,
    stylesGlobal.Top0,
    stylesGlobal.Left0,
    stylesGlobal.ZIndex1,
    stylesGlobal.DisplayFlex,
    stylesGlobal.AlignItemsCenter,
    stylesGlobal.JustifyContentCenter,
    stylesGlobal.WidthFull,
    stylesGlobal.HeightFull,
    stylesGlobal.PointerEventsNone,
    styles.TextFieldValuePlaceholder
  )
}

type TextFieldNumberProps = Omit<TextFieldProps, 'onChange' | 'value'> & {
  value: string
  onChange: (value: string) => void
  showPlaceholder?: boolean
}

const TextFieldNumber: React.FC<TextFieldNumberProps> = props => {
  const { onChange, className, color, focused, InputProps, showPlaceholder, value } = props

  const { isDesktop } = Platform

  const PlaceholderTextComponent = showPlaceholder ? (
    <span>
      <span className={styles.ColorPlaceholder}>
        {''.padStart(value === DEFAULT_PLACEHOLDER ? 8 : 8 - value.replace(/^0+/, '').length, '0')}
      </span>
      <span>{value.replace(/^0+/, '')}</span>
    </span>
  ) : undefined

  if (isDesktop)
    return (
      <div className={stylesGlobal.PositionRelative}>
        <TextField
          value={value}
          placeholder="Enter Number"
          className={className}
          InputProps={{
            classes: {
              ...InputProps?.classes,
              input: clsx(
                stylesGlobal.TextCenter,
                styles.TextFieldNumberInput,
                InputProps?.classes?.input
              )
            }
          }}
          inputProps={{ type: 'text', pattern: '[0-9]*', maxLength: 8, inputMode: 'numeric' }}
          color={color}
          focused={focused}
          size="small"
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            const target = event.target as any
            const selectionEnd = target.selectionEnd ?? 0
            const selectionStart = target.selectionStart ?? 0

            const regex = /^[0-9\b]+$/
            const isValidNumber = regex.test(event.key)
            const isBackspace = event.key === 'Backspace'
            const numSelected = selectionEnd - selectionStart

            // If pressing a number
            if (isValidNumber) {
              let newValue = ''

              if (selectionStart < CHARACTER_LIMIT) {
                newValue =
                  value.substring(0, selectionStart) +
                  event.key +
                  value.substring(selectionStart + 1)
              } else if (selectionStart === CHARACTER_LIMIT) {
                const tempValue = (value + event.key).replace(/^0+/, '')

                if (tempValue.length <= CHARACTER_LIMIT)
                  newValue = tempValue.padStart(CHARACTER_LIMIT, '0')
                else return false
              }

              onChange(newValue)
            }
            // If pressing backspace
            else if (isBackspace) {
              let newValue = ''

              // If not selecting any
              if (selectionStart === selectionEnd) {
                if (selectionStart !== 0)
                  newValue =
                    value.substring(0, selectionStart - 1) + 0 + value.substring(selectionStart)
                else return false
              }
              // If selecting certain characters
              else {
                if (numSelected === CHARACTER_LIMIT) {
                  newValue = ''.padStart(CHARACTER_LIMIT, '0')
                } else {
                  if (selectionStart === 0) newValue = ''.padStart(numSelected, '0')
                  else if (selectionStart > 0)
                    newValue = value.substring(0, selectionStart) + ''.padStart(numSelected, '0')

                  if (selectionEnd < CHARACTER_LIMIT)
                    newValue =
                      newValue + value.substring(selectionEnd, CHARACTER_LIMIT - selectionEnd)
                }
              }

              if (newValue) onChange(newValue)
            }

            /* Set cursor position */
            if (isValidNumber || isBackspace) {
              const newSelectionStart = selectionStart + (isBackspace ? (!numSelected ? -1 : 0) : 1)

              window.setTimeout(() => {
                target.setSelectionRange(newSelectionStart, newSelectionStart)
              }, 0)
            }
          }}
          fullWidth
        />

        {PlaceholderTextComponent ? (
          <div className={CLASSNAMES.PlaceholderText}>{PlaceholderTextComponent}</div>
        ) : null}
      </div>
    )

  return (
    <TextField
      value={value}
      onKeyDown={(event: any) => {}}
      placeholder="0000000"
      inputProps={TEXT_INPUT_PROPS.inputProps as TextFieldProps['inputProps']}
      InputProps={TEXT_INPUT_PROPS.InputProps}
      onChange={event => {
        const newValue = event.target.value // numeral(target.value).format('00000000')
        onChange(newValue)
      }}
      fullWidth
    />
  )
}

export default TextFieldNumber
